import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { authActions } from '@store/actions/auth.actions';
import { EMPTY, filter, map, mergeMap, switchMap, tap } from 'rxjs';
import { userActions } from '@store/actions/user.actions';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { authSelectors } from '@store/selectors/auth.selectors';
import { appActions } from '@store/actions/app.actions';
import { catchError, withLatestFrom } from 'rxjs/operators';
import { STORAGE_KEYS, StorageService } from '@core/services/storage/storage.service';
import { AcRoutingService } from '@core/services/routing/ac-routing.service';
import { AuthConfigIds } from '../../auth/auth.config';
import { BROADCAST_CHANNEL } from '@core/core.module';
import { ApiUserService } from '@core/api/services/user/api-user.service';

@Injectable()
export class AuthEffects {
  cookiesSet$ = createEffect(() =>
    this._actions$.pipe(
      ofType(appActions.setCookiesConsent),
      filter(({ necessary }) => necessary),
      map(() => authActions.startAuth())
    )
  );
  authStarted$ = createEffect(() =>
    this._actions$.pipe(
      ofType(authActions.startAuth),
      map(() => this._store.selectSignal(authSelectors.idp)()),
      switchMap((idp) => {
        if (idp) {
          return this._oidcSecurity.checkAuth(undefined, idp);
        }
        return this._oidcSecurity.checkAuth(undefined, AuthConfigIds.ONE_LOGIN).pipe(
          catchError(() => {
            return this._oidcSecurity.checkAuth(undefined, AuthConfigIds.AZURE);
          })
        );
      }),
      map((loginResponse) => {
        if (loginResponse.isAuthenticated && loginResponse.configId) {
          this._storage.setItem(STORAGE_KEYS.IDP, loginResponse.configId, 'cookie');
          return authActions.loginSuccessful({ idp: loginResponse.configId as AuthConfigIds });
        }
        const error = loginResponse?.errorMessage;
        return authActions.loginFailed({ error: error ?? 'Error login' });
      })
    )
  );
  loginSuccessful$ = createEffect(() =>
    this._actions$.pipe(
      ofType(authActions.loginSuccessful),
      map(() => userActions.fetchUser())
    )
  );
  loginFailed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(authActions.loginFailed),
        tap(({ error }) => console.error(error))
      ),
    {
      dispatch: false,
    }
  );
  attemptLogout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(authActions.attemptLogout),
      withLatestFrom(this._store.select(authSelectors.idp)),
      map(([{ idp, isMessage }, storeIdp]) => {
        return {
          idp: idp ?? storeIdp,
          isMessage,
        };
      }),
      filter(({ idp }) => idp != null),
      tap(() => this._routing.goHome()),
      mergeMap(({ idp, isMessage }) => {
        return this._oidcSecurity.logoffAndRevokeTokens(idp!).pipe(
          tap(() => {
            if (!isMessage) {
              this._broadcastChannel.postMessage('logout');
            }
          }),
          catchError(() => EMPTY)
        );
      }),
      map(() => authActions.loggedOut())
    )
  );
  loggedOut$ = createEffect(() =>
    this._actions$.pipe(
      ofType(authActions.loggedOut),
      tap(() => {
        this._storage.removeItem(STORAGE_KEYS.IDP, 'cookie');
        this._storage.removeItem(STORAGE_KEYS.COUNTRY, 'cookie');
        this._storage.removeItem(STORAGE_KEYS.ENERGY_REHABILITATION_MODE, 'session');
      }),
      mergeMap(() => [
        userActions.setEnergyRehabilitationMode({ enabled: false }),
        userActions.setImpersonation({ impersonationToken: null }),
      ])
    )
  );
  attemptLogin$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(authActions.attemptLogin),
        tap(({ idp }) => {
          const finalIdp = idp ?? this._storage.getItem(STORAGE_KEYS.IDP, 'cookie');
          if (finalIdp === AuthConfigIds.AZURE) {
            this._oidcSecurity.authorize(AuthConfigIds.AZURE);
            this._routing.goToAzure();
          } else {
            this._routing.goToOneLogin();
            this._oidcSecurity.authorize(AuthConfigIds.ONE_LOGIN);
          }
        })
      ),
    {
      dispatch: false,
    }
  );
  deleteFactors$ = createEffect(() =>
    this._actions$.pipe(
      ofType(authActions.deleteFactors),
      switchMap(() => this._apiUser.deleteFactors().pipe(catchError(() => EMPTY))),
      map(() => authActions.attemptLogout({}))
    )
  );

  constructor(
    private _actions$: Actions,
    private _oidcSecurity: OidcSecurityService,
    private _store: Store<StoreState>,
    private _storage: StorageService,
    private _routing: AcRoutingService,
    @Inject(BROADCAST_CHANNEL)
    private _broadcastChannel: BroadcastChannel,
    private _apiUser: ApiUserService
  ) {}
}
